/**
 * HIPAA-Compliant Auth Context
 * 
 * Features:
 * - Global authentication state management
 * - Login/logout functions
 * - Token management
 * - User profile state
 * - Auto-initialization on app load
 * 
 * Based on HIPAA_COMPLIANCE_COMPLETE_SECURITY_GUIDE.md
 * Frontend guide lines 467-620
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { login as apiLogin, register as apiRegister, logout as apiLogout, getCurrentUser, processOAuthCallback } from '../services/authService';
import { jwtDecode } from 'jwt-decode';

// Create Context
const AuthContext = createContext();

// Initial State
const initialState = {
  user: null,
  tokens: { 
    accessToken: null, 
    refreshToken: null, 
    csrfToken: null 
  },
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
  loadingStage: null
};

// Action Types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  CLEAR_ERROR: 'CLEAR_ERROR',
  INIT_COMPLETE: 'INIT_COMPLETE',
  SET_LOADING_STAGE: 'SET_LOADING_STAGE'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
      
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        tokens: { accessToken: null, refreshToken: null, csrfToken: null },
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        tokens: { accessToken: null, refreshToken: null, csrfToken: null },
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        tokens: {
          ...state.tokens,
          accessToken: action.payload.accessToken,
          csrfToken: action.payload.csrfToken
        }
      };
    
    case AUTH_ACTIONS.UPDATE_PROFILE:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload
        }
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.INIT_COMPLETE:
      return {
        ...state,
        isInitialized: true,
        isLoading: false
      };
    
    case AUTH_ACTIONS.SET_LOADING_STAGE:
      return {
        ...state,
        loadingStage: action.payload
      };
    
    default:
      return state;
  }
};

// Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING_STAGE, payload: 'Checking authentication...' });
        
        // 1. Check if tokens exist
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!accessToken || !refreshToken) {
          console.log('No tokens found, user not authenticated');
          dispatch({ type: AUTH_ACTIONS.INIT_COMPLETE });
          return;
        }
        
        // 2. Decode and check expiration
        try {
          const decoded = jwtDecode(accessToken);
          
          if (decoded.exp * 1000 < Date.now()) {
            console.log('Access token expired, will refresh on first request');
            // Don't clear tokens - let interceptor handle refresh
          }
        } catch (decodeError) {
          console.error('Failed to decode token:', decodeError);
          localStorage.clear();
          dispatch({ type: AUTH_ACTIONS.INIT_COMPLETE });
          return;
        }
        
        // 3. Validate token with backend
        dispatch({ type: AUTH_ACTIONS.SET_LOADING_STAGE, payload: 'Validating session...' });
        
        const user = await getCurrentUser();
        
        if (user) {
          dispatch({ 
            type: AUTH_ACTIONS.LOGIN_SUCCESS, 
            payload: { 
              user: user, 
              tokens: { 
                accessToken, 
                refreshToken,
                csrfToken: localStorage.getItem('csrfToken')
              } 
            }
          });
          console.log('User authenticated:', user.email);
        } else {
          console.log('Token validation failed, clearing tokens');
          localStorage.clear();
        }
        
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.clear();
      } finally {
        dispatch({ type: AUTH_ACTIONS.INIT_COMPLETE });
        dispatch({ type: AUTH_ACTIONS.SET_LOADING_STAGE, payload: null });
      }
    };
    
    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await apiLogin(email, password);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: response.user,
          tokens: response.tokens
        }
      });
      
      return { success: true, user: response.user };
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: message
      });
      return { success: false, error: message };
    }
  };

  // Register function
  const register = async (name, email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await apiRegister(name, email, password);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: response.user,
          tokens: response.tokens
        }
      });
      
      return { success: true, user: response.user };
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: message
      });
      return { success: false, error: message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // OAuth login (after callback)
  const oauthLogin = async (user, tokens) => {
    // Tokens already stored by processOAuthCallback
    dispatch({
      type: AUTH_ACTIONS.LOGIN_SUCCESS,
      payload: { user, tokens }
    });
  };

  // Update user profile
  const updateProfile = (updates) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_PROFILE,
      payload: updates
    });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    // State
    user: state.user,
    tokens: state.tokens,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    loadingStage: state.loadingStage,
    error: state.error,
    
    // Functions
    login,
    register,
    logout,
    oauthLogin,
    updateProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
